import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { CIVIL_REGISTRY_FAQ_CATEGORIES } from "@/content/civil-registry-corpus/faq-defs";

export const Route = createFileRoute("/civil-registry/faq/")({
  loader: async (): Promise<{ faqs: { q: string; a: string }[] }> => {
    const m = await import("@/content/civil-registry-corpus");
    return { faqs: m.CIVIL_REGISTRY_FAQ_TH };
  },
  component: CivilRegistryFaqHub,
  head: ({ loaderData }) => {
    const faqs = loaderData?.faqs ?? [];
    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: `ทะเบียนราษฎร สูติบัตร มรณบัตร ทะเบียนบ้าน เปลี่ยนชื่อ — FAQ ${faqs.length} ข้อ`,
        description:
          "คำถามที่พบบ่อยเรื่องแจ้งเกิดและสูติบัตร แจ้งตายและมรณบัตร ทะเบียนบ้าน ท.ร.14 และเล่มเหลือง ท.ร.13 เปลี่ยนชื่อตัว-ชื่อสกุล บัตรประจำตัวประชาชน ทะเบียนสมรส-หย่า รับรองบุตร เด็กเกิดต่างประเทศและสัญชาติไทย พร้อมการแปลและรับรองนิติกรณ์เพื่อใช้ต่างประเทศ",
        path: "/civil-registry/faq",
        type: "article",
        alternates: [{ lang: "en", path: "/en/civil-registry/faq" }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "FAQ ทะเบียนราษฎรและเอกสารสถานะบุคคล", url: `${site}/civil-registry/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/civil-registry/faq")) },
      ],
    };
  },
});

function CivilRegistryFaqHub() {
  const { faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span> <span>FAQ ทะเบียนราษฎรและเอกสารสถานะบุคคล</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          ทะเบียนราษฎรและเอกสารสถานะบุคคล — คำถามที่พบบ่อย {faqs.length} ข้อ
        </h1>
        <p className="mb-6 text-muted-foreground">
          คำตอบอ้างอิงพระราชบัญญัติการทะเบียนราษฎร พ.ศ. 2534 พระราชบัญญัติชื่อบุคคล พ.ศ. 2505
          พระราชบัญญัติบัตรประจำตัวประชาชน พ.ศ. 2526 พระราชบัญญัติสัญชาติ พ.ศ. 2508
          ประมวลกฎหมายแพ่งและพาณิชย์ สำนักบริหารการทะเบียน กรมการปกครอง (bora.dopa.go.th)
          และกรมการกงสุล (consular.mfa.go.th) ข้อมูลนี้เป็นข้อมูลทั่วไป ระเบียบและแนวปฏิบัติของแต่ละสำนักทะเบียนอาจแตกต่างกัน
          โปรดตรวจสอบกับหน่วยงานก่อนดำเนินการ
        </p>
        <div className="mb-6 rounded-lg border bg-muted/30 p-4 text-sm">
          <strong>English:</strong>{" "}
          <a href="/en/civil-registry/faq" className="text-primary underline">Civil registration, birth &amp; death records FAQ →</a>
        </div>
        <section aria-label="หมวดคำถาม" className="mb-8">
          <h2 className="mb-3 text-lg font-semibold">เลือกอ่านตามหมวด</h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {CIVIL_REGISTRY_FAQ_CATEGORIES.map((c) => (
              <a key={c.slug} href={`/civil-registry/faq/${c.slug}`} className="rounded-lg border bg-card p-3 text-sm transition hover:border-primary hover:shadow-sm">
                <div className="mb-1 font-semibold">{c.titleTh}</div>
                <div className="line-clamp-2 text-xs text-muted-foreground">{c.descTh}</div>
              </a>
            ))}
          </div>
        </section>
        <div className="space-y-4">
          {faqs.map((item: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">
                <span className="mr-2 text-muted-foreground">ข้อ {i + 1}.</span>
                {item.q}
              </summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>
        <section className="mt-10 rounded-lg border bg-card p-5">
          <h2 className="mb-2 text-lg font-bold">ปรึกษาทีมงาน</h2>
          <p className="text-sm text-muted-foreground">
            สอบถามรายการเอกสาร การคัดรับรองสำเนา การแปลและรับรองนิติกรณ์เอกสารทะเบียนราษฎรได้ทางโทรศัพท์ LINE หรืออีเมล จันทร์–เสาร์ 9:00–18:00
          </p>
          <a href="/contact" className="mt-3 inline-block rounded-md border px-4 py-2 text-sm font-semibold hover:bg-muted">ติดต่อเจ้าหน้าที่</a>
        </section>
        <FaqSourceNote topic="civil-registry" locale="th" />
      </main>
      <SiteFooter />
    </>
  );
}